import React from "react";
import axios from "axios";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import "./admin-teacher-calendar-connections.css";

const buildApiBaseUrl = () => {
  const envUrl =
    (
      process?.env?.REACT_APP_API_BASE_URL ||
      process?.env?.NEXT_PUBLIC_API_BASE_URL ||
      ""
    ).trim() || "";
  if (envUrl) {
    return envUrl.replace(/\/$/, "");
  }
  return (baseUrl || "").replace(/\/$/, "");
};

const getTeacherIdentifier = (teacher) =>
  teacher?.TeacherID || teacher?.MongoId || teacher?._id || teacher?.id;

const getTeacherKeyFromConnection = (connection) => {
  if (!connection?.teacherId) return "";
  try {
    return connection.teacherId.toString().toLowerCase();
  } catch (_err) {
    return "";
  }
};

const AdminTeacherCalendarConnections = () => {
  const [connections, setConnections] = React.useState([]);
  const [loadingConnections, setLoadingConnections] = React.useState(false);
  const [connectionsError, setConnectionsError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [rowBusy, setRowBusy] = React.useState({});
  const [rowError, setRowError] = React.useState({});
  const [teachers, setTeachers] = React.useState({});
  const [teacherError, setTeacherError] = React.useState("");
  const [loadingTeachers, setLoadingTeachers] = React.useState(false);

  const apiBase = React.useMemo(() => buildApiBaseUrl(), []);

  const fetchConnections = React.useCallback(async () => {
    setLoadingConnections(true);
    setConnectionsError("");
    try {
      const { data } = await axios.get(
        `${apiBase}/api/integrations/nylas/connections`,
        getRequestHeaders()
      );
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.connections)
        ? data.connections
        : [];
      setConnections(
        list.map((item) => {
          const teacherId =
            item.teacherId || item.teacherID || item.teacher_id || null;
          return {
            ...item,
            teacherId: teacherId ? teacherId.toString() : null,
            teacherKey: teacherId
              ? teacherId.toString().toLowerCase()
              : getTeacherKeyFromConnection(item),
            nylasEmail:
              item.email ||
              item.nylasEmail ||
              item.connectedEmail ||
              item.connectionEmail ||
              "",
            connectedAt:
              item.connectedAt || item.updatedAt || item.createdAt || item.lastSynced,
          };
        })
      );
    } catch (error) {
      console.error("[calendar connections] list", error);
      setConnections([]);
      setConnectionsError(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to load calendar connections."
      );
    } finally {
      setLoadingConnections(false);
    }
  }, [apiBase]);

  React.useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const fetchTeachers = React.useCallback(async () => {
    setLoadingTeachers(true);
    setTeacherError("");
    try {
      const { data } = await axios.get(
        `${baseUrl}/initialadminrequest`,
        getRequestHeaders()
      );
      const teacherMap = {};
      if (Array.isArray(data)) {
        data.forEach((teacher) => {
          const possibleIds = [
            teacher?.MongoId,
            teacher?._id,
            teacher?.TeacherID,
            getTeacherIdentifier(teacher),
          ]
            .filter(Boolean)
            .map((value) => value.toString().toLowerCase());
          new Set(possibleIds).forEach((key) => {
            teacherMap[key] = teacher;
          });
        });
      }
      setTeachers(teacherMap);
    } catch (error) {
      console.error("[calendar connections] teachers", error);
      setTeachers({});
      setTeacherError(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to load teacher details."
      );
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  React.useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const filteredConnections = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return connections;
    return connections.filter((connection) => {
      const teacherRecord =
        teachers[connection.teacherKey || getTeacherKeyFromConnection(connection)] ||
        {};
      const combine = [
        teacherRecord?.Name,
        teacherRecord?.Email,
        teacherRecord?.School,
        connection.nylasEmail,
        connection.teacherId,
      ]
        .filter(Boolean)
        .map((val) => val.toString().toLowerCase());
      return combine.some((value) => value.includes(query));
    });
  }, [connections, search, teachers]);

  const formatDate = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  };

  const handleDisconnectConnection = React.useCallback(
    async (connection) => {
      const teacherId = connection.teacherId;
      const teacherKey =
        teacherId || connection.teacherEmail || connection.nylasEmail;
      if (!teacherId || !teacherKey) return;
      setRowBusy((prev) => ({ ...prev, [teacherKey]: true }));
      setRowError((prev) => ({ ...prev, [teacherKey]: "" }));
      try {
        await axios.post(
          `${apiBase}/api/integrations/nylas/disconnect`,
          { teacherId },
          getRequestHeaders()
        );
        await fetchConnections();
      } catch (error) {
        console.error("[calendar connections] disconnect", teacherId, error);
        setRowError((prev) => ({
          ...prev,
          [teacherKey]:
            error?.response?.data?.error ||
            error?.message ||
            "Unable to disconnect calendar.",
        }));
      } finally {
        setRowBusy((prev) => ({ ...prev, [teacherKey]: false }));
      }
    },
    [apiBase, fetchConnections]
  );

  return (
    <div className="calendar-connections">
      <div className="calendar-connections__header">
        <div>
          <h1>Calendar Connections</h1>
          <p>
            Review all active Nylas connections. Disconnect teachers who no
            longer need sync or confirm who is currently connected.
          </p>
        </div>
        <div className="calendar-connections__header-actions">
          <input
            type="search"
            placeholder="Search by name, email, or ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button type="button" onClick={fetchConnections} disabled={loadingConnections}>
            {loadingConnections ? "Refreshing…" : "Refresh Connections"}
          </button>
        </div>
      </div>

      {connectionsError && (
        <div className="calendar-connections__error">{connectionsError}</div>
      )}

      <div className="calendar-connections__table-card">
        {filteredConnections.length === 0 && !loadingConnections && (
          <div className="calendar-connections__empty">
            {search
              ? "No connected teachers match your search."
              : "No calendar connections have been created yet."}
          </div>
        )}
        <table className="calendar-connections__table">
          <thead>
            <tr>
              <th>Teacher</th>
              <th>Teacher Email</th>
              <th>Connected As</th>
              <th>Connected On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingConnections && (
              <tr>
                <td colSpan={5} className="text-center py-6">
                  Loading connections…
                </td>
              </tr>
            )}
            {filteredConnections.map((connection) => {
              const key =
                connection.teacherKey ||
                connection.teacherId ||
                connection.teacherEmail ||
                connection.nylasEmail;
              const busy = !!rowBusy[key];
              const teacherRecord =
                teachers[connection.teacherKey || getTeacherKeyFromConnection(connection)] ||
                {};
              const teacherName =
                teacherRecord?.Name || connection.teacherName || "Unknown";
              const teacherEmail =
                teacherRecord?.Email ||
                connection.teacherEmail ||
                "—";
              const teacherSchool =
                teacherRecord?.School || connection.school || "";
              return (
                <tr key={key}>
                  <td>
                    <div className="calendar-connections__teacher">
                      <div className="calendar-connections__teacher-name">
                        {teacherName}
                      </div>
                      <div className="calendar-connections__teacher-meta">
                        #{connection.teacherId || "n/a"} ·{" "}
                        {(teacherSchool || "School?").toUpperCase()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="calendar-connections__teacher-email">
                      {teacherEmail || "—"}
                    </div>
                  </td>
                  <td>
                    <div className="calendar-connections__chip calendar-connections__chip--success">
                      Connected
                    </div>
                    {connection.nylasEmail && (
                      <div className="calendar-connections__chip-detail">
                        {connection.nylasEmail}
                      </div>
                    )}
                    {rowError[key] && (
                      <div className="calendar-connections__chip-error">
                        {rowError[key]}
                      </div>
                    )}
                  </td>
                  <td>{formatDate(connection.connectedAt)}</td>
                  <td>
                    <div className="calendar-connections__row-actions">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => handleDisconnectConnection(connection)}
                        disabled={busy}
                      >
                        {busy ? "Disconnecting…" : "Disconnect"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="calendar-connections__footer">
        Showing {filteredConnections.length} connection
        {filteredConnections.length === 1 ? "" : "s"} (total {connections.length})
      </div>
      {teacherError && (
        <div className="calendar-connections__error mt-2">{teacherError}</div>
      )}
      {loadingTeachers && (
        <div className="calendar-connections__footer">Loading teacher info…</div>
      )}
    </div>
  );
};

export default AdminTeacherCalendarConnections;
